<script lang="ts">
    import tabIcon from "@/assets/gear.svg";
    import { Leverantor } from "@/store/modules/leverantorModule";
    import { getConfig, klinikenApi } from "@/api";
    import { get } from "lodash";

    export default {
        name: "ListLeverantor",
        tabName: "Leverantörer",
        icon: tabIcon,
    };
</script>

<script setup lang="ts">
    import CollapsibleSection from "@/components/generic/CollapsibleSection.vue";
    import MaterialForm from "@/tabs/Admin/forms/MaterialForm.vue";
    import LeverantorForm from "@/tabs/Admin/forms/LeverantorForm.vue";
    import ListMaterialgrupp from "@/tabs/Admin/ListMaterialgrupp/ListMaterialgrupp.vue";
    import { ref, onBeforeUnmount, onBeforeMount, computed, toRefs } from "vue";
    import { openDialog } from "@/utils";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import KlinikenTable from "@/components/Table/KlinikenTable.vue";
    import CustomButton from "@/components/Button.vue";
    import { useStore } from "@/store";

    const store = useStore();

    const props = defineProps<{
        tabId: number;
    }>();

    const columns = [
        {
            title: "Namn",
            name: "namn",
            sort: "namn",
        },
    ];
    const dropdownActions: {
        name: string;
        title: string;
        show: () => boolean;
    }[] = [
        {
            name: "editLeverantor",
            title: "Redigera leverantör",
            show: () => true,
        },
    ];

    const leverantorer = computed<Leverantor[]>(() => {
        return store.getters["leverantor/leverantorerSubset"]({
            subsetName: subsetName.value,
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        });
    });

    const nyaLeverantorer = computed<Leverantor[]>(() =>
        store.getters["leverantor/leverantorerSubset"]({
            subsetName: "newlyCreated",
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        })
    );
    const redigeradeLeverantorer = computed<Leverantor[]>(() =>
        store.getters["leverantor/leverantorerSubset"]({
            subsetName: "newlyUpdated",
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        })
    );

    const listPage = ref<number>(1);
    const listCount = ref<number>(0);
    const listLimit = ref<number>(20);
    const filterParams = ref<
        | {
              [key: string]: string;
          }
        | undefined
    >();
    const subsetName = ref<string>("default");

    const { tabId } = toRefs(props);

    defineExpose({
        tabId,
    });

    const onChangeLimit = async (newLimit: number) => {
        listLimit.value = newLimit;

        await fetchLeverantorer({
            limit: newLimit,
            page: 1,
        });
    };

    const onLoadPage = async (page: number) => {
        await fetchLeverantorer({ page });
    };

    const openCreateLeverantor = () => {
        store.dispatch("tabs/openTab", {
            component: LeverantorForm,
        });
    };

    const openEditLeverantor = (leverantor: Leverantor) => {
        store.dispatch("tabs/openTab", {
            component: LeverantorForm,
            parameters: {
                leverantorId: leverantor.id,
            },
        });
    };

    const onSortChange = async (newSortBy: string) => {
        filterParams.value = {
            ...(filterParams.value ? filterParams.value : {}),
            ordering: newSortBy,
        };

        await fetchLeverantorer({
            page: 1,
        });
    };

    const fetchLeverantorer = async ({ page, limit = listLimit.value }: { page: number; limit?: number }) => {
        try {
            const {
                nextPage,
                total,
                subsetName: fetchedSubsetName,
            } = await store.dispatch("leverantor/fetchLeverantorer", {
                page,
                limit,
                filterParams: filterParams.value,
            });

            listPage.value = page;
            subsetName.value = fetchedSubsetName;

            if (total) {
                listCount.value = total;
            }
        } catch (_) {
            console.log(_);
            openDialog("Kunde inte hämta leverantörer", "error");
        }
    };

    onBeforeMount(async () => {
        await fetchLeverantorer({ page: listPage.value });
        await store.dispatch("leverantor/addSubscriber");
    });

    onBeforeUnmount(async () => {
        await store.dispatch("leverantor/removeSubscriber");
    });
</script>

<template>
    <div class="list-material">
        <admin-lock-border />
        <div class="header">
            <h1>Leverantörer</h1>
            <CustomButton type="button" variant="hollow" @click="openCreateLeverantor" :style="{ padding: 0 }">
                <img src="@/assets/plus_circle_blue.svg" />
                <span>Skapa leverantör</span>
            </CustomButton>
        </div>
        <div class="newly" v-if="redigeradeLeverantorer.length > 0">
            <collapsible-section isSingle :isExpanded="false" heading="Nyligen redigerade leverantörer">
                <kliniken-table
                    :items="redigeradeLeverantorer"
                    :columns="columns"
                    @editMaterial="openEditLeverantor"
                    @createMaterial="openCreateLeverantor"
                    :has-dropdown="true"
                    :dropdown-actions="dropdownActions"
                />
            </collapsible-section>
        </div>
        <div class="newly-created" v-if="nyaLeverantorer.length > 0">
            <collapsible-section isSingle :isExpanded="false" heading="Nyligen tillagda leverantörer">
                <kliniken-table
                    :items="nyaLeverantorer"
                    :columns="columns"
                    @editMaterial="openEditLeverantor"
                    @createMaterial="openCreateLeverantor"
                    :has-dropdown="true"
                    :dropdown-actions="dropdownActions"
                />
            </collapsible-section>
        </div>
        <div>
            <kliniken-table
                :items="leverantorer"
                :columns="columns"
                :count="listCount"
                :currentPage="listPage"
                :limit="listLimit"
                paginationStyle="paging"
                @sort="onSortChange"
                @loadPage="onLoadPage"
                @changeLimit="onChangeLimit"
                @editLeverantor="openEditLeverantor"
                @createLeverantor="openCreateLeverantor"
                :showBottom="true"
                :showLimitDropdown="true"
                :has-dropdown="true"
                :dropdown-actions="dropdownActions"
            />
        </div>
    </div>
</template>

<style lang="scss">
    .list-material {
        .header {
            h1 {
                margin: 0;
            }

            display: flex;
            gap: 20px;
            align-items: center;
        }

        .newly {
            margin-top: 1rem;
        }
    }
</style>
